import { useEffect } from 'react';
import type { SignalFeedFocus } from './lib/mediaSignalFeed';

/**
 * News-focus bridge (#183 corroboration chip → the News & Social tab).
 *
 * Clicking "N social reports nearby" on an incident opens the News & Social
 * surface filtered to that incident's bbox. The chip lives in the COP detail
 * rail; the surface has TWO homes (the COP left rail + the operations panel),
 * possibly in different windows — so the focus travels the same dual path as
 * the selection bridge: a same-window CustomEvent (BroadcastChannel never
 * delivers to the posting window) plus a same-origin BroadcastChannel for any
 * open sibling window. Every listening home applies it: the rail switches to
 * its News tab, the panel flips its region tab, and the feed itself adopts the
 * bbox + shows a clearable focus chip.
 *
 * Transient by design — focus is navigation, not stored state. Nothing
 * persists; clearing the chip just drops the bbox.
 */

export const NEWS_FOCUS_EVENT = 'tsr:news-focus';
export const NEWS_FOCUS_CHANNEL = 'tsr-news-focus';

interface FocusMessage {
  type: 'news:focus';
  focus: SignalFeedFocus;
}

function isFocus(v: unknown): v is SignalFeedFocus {
  if (typeof v !== 'object' || v === null) return false;
  const f = v as Record<string, unknown>;
  return (
    typeof f.west === 'number' &&
    typeof f.south === 'number' &&
    typeof f.east === 'number' &&
    typeof f.north === 'number' &&
    typeof f.label === 'string'
  );
}

/** Send a focus to every News & Social home — this window and siblings. */
export function focusNewsTab(focus: SignalFeedFocus): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<SignalFeedFocus>(NEWS_FOCUS_EVENT, { detail: focus }));
  }
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(NEWS_FOCUS_CHANNEL);
    channel.postMessage({ type: 'news:focus', focus } satisfies FocusMessage);
    channel.close();
  }
}

/** Run `onFocus` whenever a focus arrives — same window or a sibling. */
export function useNewsFocus(onFocus: (focus: SignalFeedFocus) => void): void {
  useEffect(() => {
    const channel =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(NEWS_FOCUS_CHANNEL);
    if (channel) {
      channel.onmessage = (e: MessageEvent) => {
        const data = e.data as FocusMessage | undefined;
        if (data?.type === 'news:focus' && isFocus(data.focus)) onFocus(data.focus);
      };
    }
    const onEvent = (e: Event): void => {
      const detail = (e as CustomEvent<SignalFeedFocus>).detail;
      if (isFocus(detail)) onFocus(detail);
    };
    if (typeof window !== 'undefined') window.addEventListener(NEWS_FOCUS_EVENT, onEvent);
    return () => {
      channel?.close();
      if (typeof window !== 'undefined') window.removeEventListener(NEWS_FOCUS_EVENT, onEvent);
    };
  }, [onFocus]);
}
